import { create } from "zustand";
import type { Storyboard } from "@/utils/types";

/**
 * Sanitize component name for use in code:
 * - Remove spaces (convert to PascalCase)
 * - Ensure first letter is capitalized
 */
function sanitizeComponentName(name: string): string {
  if (!name) return "";

  // Split by spaces and capitalize each word, then join
  const words = name.split(/\s+/).filter(Boolean);
  const pascalCase = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  return pascalCase;
}

export interface PublishComponentModalStore {
  // Modal state
  isOpen: boolean;
  isProcessing: boolean;
  progress: number; // 0-100
  progressMessage: string;
  componentId: string | null; // ID returned from backend
  error: string | null;

  // Storyboard context for publishing
  storyboard: Storyboard | null;
  componentName: string; // User-entered component name

  // Actions
  openModal: (storyboard: Storyboard) => void;
  closeModal: () => void;
  setComponentName: (name: string) => void;
  startPublish: () => void;
  setProgress: (progress: number, message: string) => void;
  setComponentId: (id: string) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  isOpen: false,
  isProcessing: false,
  progress: 0,
  progressMessage: "",
  componentId: null,
  error: null,
  storyboard: null,
  componentName: "",
};

export const usePublishComponentModalStore = create<PublishComponentModalStore>(
  (set) => ({
    ...initialState,

    openModal: (storyboard: Storyboard) => {
      set({
        ...initialState,
        isOpen: true,
        storyboard,
        componentName: sanitizeComponentName(storyboard.name || ""),
      });
    },

    closeModal: () => {
      set(initialState);
    },

    setComponentName: (name: string) => {
      set({ componentName: sanitizeComponentName(name) });
    },

    startPublish: () => {
      set({
        isProcessing: true,
        progress: 0,
        progressMessage: "Starting...",
        error: null,
        componentId: null,
      });
    },

    setProgress: (progress: number, message: string) => {
      set({ progress, progressMessage: message });
    },

    setComponentId: (id: string) => {
      set({
        componentId: id,
        isProcessing: false,
        progress: 100,
        progressMessage: "Complete!",
      });
    },

    setError: (error: string | null) => {
      set({
        error,
        isProcessing: false,
      });
    },

    reset: () => {
      set(initialState);
    },
  }),
);
